import { useTranslation } from 'react-i18next';

import { useGetGeneralOrganizationAddresses } from '@/api/generated/general-organizations/general-organizations';
import { Badge, EmptyState, Panel, Table } from '@/shared/ui';

export interface OrganizationAddressesPanelProps {
  /** Stabilní identita právě vybrané organizace; `null` = žádná není vybraná. */
  partyId: number | null;
}

/**
 * Read-only panel adres organizace. Plní se podle **právě vybrané** organizace, ne podle uloženého
 * záznamu konzumenta — po výběru i po přehození se obsah načte znovu ještě před uložením.
 * Adresy se tu needitují; mění se v organizaci a propisují se sem automaticky.
 */
export function OrganizationAddressesPanel({ partyId }: OrganizationAddressesPanelProps) {
  const { t } = useTranslation('general');

  const addressesQuery = useGetGeneralOrganizationAddresses(partyId ?? 0, {
    query: { enabled: partyId != null },
  });
  const items = addressesQuery.data?.items ?? [];

  // Podtitulek i patička platí jen tam, kde je co přebírat — bez vybrané organizace by tvrdily nepravdu.
  return (
    <Panel
      variant="filled"
      icon="pin"
      title={t('addresses.title')}
      subtitle={partyId == null ? undefined : t('addressBook.readOnly')}
      footer={partyId == null ? undefined : t('addressBook.managedOnOrganization')}
    >
      {partyId == null ? (
        <EmptyState
          icon="pin"
          title={t('addresses.noOrganization')}
          description={t('addressBook.noOrganizationHint')}
        />
      ) : addressesQuery.isLoading ? (
        <p className="text-phx-text-muted text-phx-sm">{t('addressBook.loading')}</p>
      ) : addressesQuery.isError ? (
        // Sem spadne i 403 uživatele bez oprávnění `general.organizations` — detail konzumenta
        // musí zůstat použitelný, panel jen ohlásí, že data nedostal.
        <EmptyState
          icon="alert"
          title={t('addressBook.loadError')}
          description={t('addressBook.loadErrorHint')}
        />
      ) : items.length === 0 ? (
        <EmptyState icon="pin" title={t('addresses.empty')} />
      ) : (
        <div className="border-phx-border rounded-phx overflow-x-auto border">
          <Table density="dense">
            <Table.Head>
              <Table.Row>
                <Table.HCell density="dense">{t('addresses.cols.type')}</Table.HCell>
                <Table.HCell density="dense">{t('addresses.cols.street')}</Table.HCell>
                <Table.HCell density="dense">{t('addresses.cols.city')}</Table.HCell>
                <Table.HCell density="dense">{t('addresses.cols.zipCode')}</Table.HCell>
                <Table.HCell density="dense">{t('addresses.cols.country')}</Table.HCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.addressId}>
                  <Table.Cell density="dense">
                    {item.flags.length === 0 ? (
                      '—'
                    ) : (
                      <span className="gap-phx-1_5 flex flex-wrap">
                        {/* Klíčem je index — `AddressFlag.Name` není v číselníku unikátní. */}
                        {item.flags.map((flag, index) => (
                          <Badge key={index}>{flag}</Badge>
                        ))}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell density="dense">{item.street ?? '—'}</Table.Cell>
                  <Table.Cell density="dense">{item.city ?? '—'}</Table.Cell>
                  <Table.Cell density="dense" className="whitespace-nowrap">
                    {item.zipCode ?? '—'}
                  </Table.Cell>
                  <Table.Cell density="dense">{item.country ?? '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Panel>
  );
}
